import { useEffect, useState, useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Brush, ReferenceLine } from "recharts";
import { getPlayerMvpHistory } from "@/lib/api";
import { TrendingUp, TrendingDown } from "lucide-react";

function VelocityTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload || {};
  return (
    <div className="bg-[#0f1623] border border-border/50 rounded-sm px-3 py-2 shadow-lg">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {p.date && <p className="text-[10px] text-muted-foreground/70 mb-1 font-mono">{p.date}</p>}
      {payload.map((s, i) => (
        <p key={i} className="text-sm font-mono" style={{ color: s.color }}>
          {s.name}: {s.value == null ? "—" : Number(s.value).toFixed(1)}
        </p>
      ))}
      {p.rank > 0 && (
        <p className="text-[10px] text-muted-foreground/70 mt-1 font-mono">
          Rank #{p.rank} of {p.total_players}
        </p>
      )}
    </div>
  );
}

export default function MvpVelocityChart({ playerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerId) return;
    setLoading(true);
    getPlayerMvpHistory(playerId)
      .then((res) => setData(res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [playerId]);

  const chartData = useMemo(() => {
    if (!data?.history) return [];
    const counts = {};
    return data.history.map((h) => {
      const short = h.date ? h.date.slice(5) : "";
      const base = h.raid_name || "Raid";
      let label = short ? `${base} (${short})` : base;
      counts[label] = (counts[label] || 0) + 1;
      if (counts[label] > 1) label = `${label} #${counts[label]}`;
      return {
        label,
        date: h.date,
        score: h.score,
        top_score: h.top_score,
        avg_score: h.avg_score,
        rank: h.rank,
        total_players: h.total_players,
      };
    });
  }, [data]);

  const summary = useMemo(() => {
    if (chartData.length < 2) return null;
    const first = chartData[0].score || 0;
    const last = chartData[chartData.length - 1].score || 0;
    const delta = last - first;
    const avg = chartData.reduce((s, d) => s + (d.score || 0), 0) / chartData.length;
    const best = chartData.reduce((b, d) => (d.score > (b?.score || 0) ? d : b), null);
    return { delta, avg: Math.round(avg * 10) / 10, best };
  }, [chartData]);

  if (loading) return null;
  if (!data || chartData.length === 0) return null;

  return (
    <div className="mb-6" data-testid="mvp-velocity-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="heading text-lg font-semibold uppercase tracking-tight">MVP Velocity</h3>
        {summary && (
          <div className="flex items-center gap-4 text-xs font-mono">
            <span className="text-muted-foreground">
              Avg <span className="text-foreground font-bold">{summary.avg}</span>
            </span>
            {summary.best && (
              <span className="text-muted-foreground">
                Best <span className="text-amber-400 font-bold">{summary.best.score}</span>
                <span className="text-muted-foreground/70 ml-1">({summary.best.label})</span>
              </span>
            )}
            <span className={`flex items-center gap-0.5 ${summary.delta >= 0 ? "text-green-500" : "text-destructive"}`}>
              {summary.delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {summary.delta >= 0 ? "+" : ""}{summary.delta.toFixed(1)} pts overall
            </span>
          </div>
        )}
      </div>

      <div className="stat-card p-4" data-testid="mvp-velocity-chart">
        {chartData.length === 1 ? (
          <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
            One raid scored — {chartData[0].score} pts ({chartData[0].label}). Need 2+ raids for a trend.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={chartData.length >= 6 ? 280 : 240}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} angle={-30} textAnchor="end" height={60} interval={0} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip content={<VelocityTooltip />} cursor={{ stroke: "#475569", strokeDasharray: "3 3" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={70} stroke="#475569" strokeDasharray="3 3" label={{ value: "MVP threshold", fill: "#64748b", fontSize: 9, position: "right" }} />
              <Line type="monotone" dataKey="score" stroke="#fbbf24" name="Your score"
                strokeWidth={2} dot={{ r: 4, fill: "#fbbf24" }} activeDot={{ r: 6 }}
                connectNulls isAnimationActive={false} />
              <Line type="monotone" dataKey="top_score" stroke="#a78bfa" name="Top of raid"
                strokeWidth={1.5} dot={{ r: 3, fill: "#a78bfa" }} strokeDasharray="4 3"
                connectNulls isAnimationActive={false} />
              <Line type="monotone" dataKey="avg_score" stroke="#94a3b8" name="Raid average"
                strokeWidth={1.5} dot={false} strokeDasharray="2 4"
                connectNulls isAnimationActive={false} />
              {chartData.length >= 6 && (
                <Brush dataKey="label" height={22} stroke="#475569" fill="rgba(15,22,35,0.6)"
                  travellerWidth={8} tickFormatter={() => ""} />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
